import React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { useQuery } from "@tanstack/react-query";
import { getFeedZipCodes } from "../../../../../../services/UserFeedService";

const ZipCodeInput = ({ zipCodes, onZipCodesChange }) => {
    const [zipInput, setZipInput] = React.useState("");
    const [error, setError] = React.useState("");

    const { data: zipCodeOptions = [] } = useQuery({
        queryKey: ["feedZipCodes"],
        queryFn: getFeedZipCodes,
        select: (data) => {
            if (data?.data && Array.isArray(data.data)) {
                return data.data;
            }
            // Fallback to empty array
            return [];
        },
    });

    const validateZipCode = (zip) => {
        // Only allow 5-digit numbers
        return /^\d{5}$/.test(zip);
    };

    const handleZipAdd = (event, value) => {
        // Validate zip codes and collect invalid ones
        const invalidZips = value.filter((zip) => !validateZipCode(zip));
        if (invalidZips.length > 0) {
            setError("Zip code must be 5 digits");
        } else {
            setError("");
        }
        // Filter out invalid zip codes
        const validZips = value.filter((zip) => validateZipCode(zip));

        // Remove duplicates
        const uniqueZips = validZips.filter((zip, index) => validZips.indexOf(zip) === index);

        onZipCodesChange(uniqueZips);
        setZipInput("");
    };

    const handleZipDelete = (zip) => {
        onZipCodesChange(zipCodes.filter((z) => z !== zip));
    };

    // Reset both selected values and input value
    React.useEffect(() => {
        if (!zipCodes || zipCodes.length === 0) {
            setZipInput("");
            setError("");
        }
    }, [zipCodes]);

    return (
        <div>
            <label className="tw-block tw-mb-1 tw-text-[14px] tw-text-[#555E67]">Zip Code</label>
            <Autocomplete
                multiple
                freeSolo
                options={zipCodeOptions}
                value={zipCodes || []}
                onChange={handleZipAdd}
                inputValue={zipInput}
                onInputChange={(_, newInputValue) => {
                    // Only allow numbers
                    const numericValue = newInputValue.replace(/[^0-9]/g, "").slice(0, 5);
                    setZipInput(numericValue);
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            key={option}
                            label={option}
                            {...getTagProps({ index })}
                            onDelete={() => handleZipDelete(option)}
                            sx={{
                                background: "#FFE3D1",
                                color: "#E26A2C",
                                fontWeight: 500,
                                fontSize: 14,
                            }}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Type or select zip code"
                        size="small"
                        error={!!error}
                        helperText={error}
                        inputProps={{
                            ...params.inputProps,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            maxLength: 5,
                        }}
                        InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }}
                    />
                )}
            />
        </div>
    );
};

export default ZipCodeInput;
